import {
	Badge,
	Box,
	Button,
	Flex,
	FormControl,
	FormLabel,
	Grid,
	Icon,
	Input,
	Spinner,
	Switch,
	Text,
	useColorModeValue
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { MdCloud, MdHub, MdPayments } from 'react-icons/md';
import Card from '../../components/card/Card';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { RootState } from '../../store/store';
import { fetchIntegrations, saveIntegration } from '../../store/features/integration/integration.slice';
import { Integration, IntegrationType, UpdateIntegrationPayload } from '../../models/integration.model';
import { toastService } from '../../services/toast.service';
import LeilaoConfigCard from './components/LeilaoConfigCard';

const META: Record<IntegrationType, { title: string; icon: any; desc: string }> = {
	NOVAVIDA: { title: 'NovaVida', icon: MdCloud, desc: 'Provedor de dados cadastrais (CPF).' },
	ABACATEPAY: { title: 'AbacatePay', icon: MdPayments, desc: 'Gateway de pagamentos PIX.' },
	WESEND: {
		title: 'Wesend',
		icon: MdHub,
		desc: 'Base de leads do Wesend. Consultada antes da NovaVida (sem custo de provedor).'
	}
};

function IntegrationCard({ integration }: { integration: Integration }) {
	const dispatch = useAppDispatch();
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');
	const tileBg = useColorModeValue('secondaryGray.300', 'navy.900');

	const meta = META[integration.type];
	const isNovaVida = integration.type === 'NOVAVIDA';
	// AbacatePay autentica só por token (API key), sem login/senha.
	const isAbacate = integration.type === 'ABACATEPAY';
	const [form, setForm] = useState<UpdateIntegrationPayload>({
		login: integration.login || '',
		password: '',
		apiKey: '',
		active: integration.active
	});
	const [costPerQuery, setCostPerQuery] = useState<string>(
		integration.extra?.costPerQuery !== undefined && integration.extra?.costPerQuery !== null
			? String(integration.extra.costPerQuery)
			: ''
	);
	// NovaVida usa "Cliente" (extra.cliente) em vez de API Key.
	const [cliente, setCliente] = useState<string>(integration.extra?.cliente ?? '');

	const save = () => {
		const payload: UpdateIntegrationPayload = { active: form.active };
		// AbacatePay não usa login/senha — envia apenas o token.
		if (!isAbacate) {
			payload.login = form.login;
			if (form.password) payload.password = form.password;
		}
		if (!isNovaVida && form.apiKey) payload.apiKey = form.apiKey;
		if (isNovaVida) {
			const extra: Record<string, any> = {};
			if (cliente !== '') extra.cliente = cliente;
			if (costPerQuery !== '') {
				const cost = Number(costPerQuery);
				if (Number.isNaN(cost) || cost < 0) {
					toastService.warning('O custo por consulta não pode ser negativo');
					return;
				}
				extra.costPerQuery = cost;
			}
			if (Object.keys(extra).length > 0) payload.extra = extra;
		}
		dispatch(saveIntegration({ type: integration.type, data: payload }));
		setForm({ ...form, password: '', apiKey: '' });
	};

	return (
		<Card>
			<Flex justify='space-between' align='center' mb='16px'>
				<Flex align='center' gap='12px'>
					<Flex align='center' justify='center' w='48px' h='48px' borderRadius='14px' bg={tileBg} color='brand.500'>
						<Icon as={meta.icon} w='24px' h='24px' />
					</Flex>
					<Box>
						<Text fontWeight='800' color={textColor}>
							{meta.title}
						</Text>
						<Text fontSize='xs' color={subColor}>
							{meta.desc}
						</Text>
					</Box>
				</Flex>
				<Badge colorScheme={integration.active ? 'green' : 'gray'} borderRadius='8px'>
					{integration.active ? 'Ativa' : 'Inativa'}
				</Badge>
			</Flex>

			{!isAbacate && (
				<>
					<FormControl mb='12px'>
						<FormLabel fontSize='sm'>{isNovaVida ? 'Usuário' : 'Login'}</FormLabel>
						<Input variant='main' value={form.login} onChange={e => setForm({ ...form, login: e.target.value })} />
					</FormControl>
					<FormControl mb='12px'>
						<FormLabel fontSize='sm'>
							Senha {integration.hasPassword && <Text as='span' color='green.500' fontSize='xs'>(configurada)</Text>}
						</FormLabel>
						<Input
							variant='main'
							type='password'
							placeholder={integration.hasPassword ? '••••••••' : ''}
							value={form.password}
							onChange={e => setForm({ ...form, password: e.target.value })}
						/>
					</FormControl>
				</>
			)}

			{isNovaVida ? (
				<FormControl mb='12px'>
					<FormLabel fontSize='sm'>Cliente</FormLabel>
					<Input
						variant='main'
						placeholder='Código/identificador do cliente NovaVida'
						value={cliente}
						onChange={e => setCliente(e.target.value)}
					/>
				</FormControl>
			) : (
				<FormControl mb='12px'>
					<FormLabel fontSize='sm'>
						{isAbacate ? 'Token' : 'API Key'}{' '}
						{integration.hasApiKey && <Text as='span' color='green.500' fontSize='xs'>(configurado)</Text>}
					</FormLabel>
					<Input
						variant='main'
						type='password'
						placeholder={integration.hasApiKey ? '••••••••' : 'Token da AbacatePay'}
						value={form.apiKey}
						onChange={e => setForm({ ...form, apiKey: e.target.value })}
					/>
				</FormControl>
			)}

			{isNovaVida && (
				<FormControl mb='12px'>
					<FormLabel fontSize='sm'>Custo por consulta (R$)</FormLabel>
					<Input
						variant='main'
						inputMode='decimal'
						placeholder='Ex: 0.15 — usado no cálculo de custo/ROI do dashboard'
						value={costPerQuery}
						onChange={e => {
							const v = e.target.value;
							if (v === '' || /^\d*\.?\d*$/.test(v)) setCostPerQuery(v);
						}}
					/>
					<Text fontSize='xs' color={subColor} mt='4px'>
						Custo cobrado pela NovaVida em cada consulta nova. Vazio = valor padrão do sistema.
					</Text>
				</FormControl>
			)}

			<Flex justify='space-between' align='center' mt='12px'>
				<Flex align='center' gap='8px'>
					<Switch
						variant='main'
						colorScheme='blue'
						isChecked={form.active}
						onChange={e => setForm({ ...form, active: e.target.checked })}
					/>
					<Text fontSize='sm' color={form.active ? 'brand.500' : subColor} fontWeight='600'>
						{form.active ? 'Ativado' : 'Desativado'}
					</Text>
				</Flex>
				<Button variant='brand' onClick={save}>
					Salvar
				</Button>
			</Flex>
		</Card>
	);
}

export default function Integracoes() {
	const dispatch = useAppDispatch();
	const { list, loading } = useAppSelector((state: RootState) => state.integration);

	useEffect(() => {
		dispatch(fetchIntegrations());
	}, [dispatch]);

	const byType = (type: IntegrationType): Integration =>
		list.find(i => i.type === type) || { type, active: false };

	return (
		<Box pt='10px'>
			{loading && list.length === 0 && <Spinner color='brand.500' />}
			<Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap='20px'>
				{/* Wesend não aparece aqui: a conexão é interna (env do datric-server). */}
				<IntegrationCard integration={byType('NOVAVIDA')} />
				<IntegrationCard integration={byType('ABACATEPAY')} />
				{/* Config do leilão: não é credencial, mas é a mesma tela de setup do admin. */}
				<LeilaoConfigCard />
			</Grid>
		</Box>
	);
}
